import { Injectable } from "@angular/core";
import { FileInfo, FileProperties, MIME_TYPE_FOLDER, MIME_TYPE_SHORTCUT } from "../models/fileInfo";
import type { JtmsDriveAppProperties } from "../models/drive-app-properties";

const FIELDS = [
  "appProperties",
  "capabilities",
  "createdTime",
  "description",
  "driveId",
  "explicitlyTrashed",
  "exportLinks",
  "fileExtension",
  "fullFileExtension",
  "hasThumbnail",
  "headRevisionId",
  "iconLink",
  "id",
  "imageMediaMetadata",
  "kind",
  "lastModifyingUser",
  "mimeType",
  "modifiedByMe",
  "modifiedByMeTime",
  "modifiedTime",
  "name",
  "originalFilename",
  "ownedByMe",
  "owners",
  "parents",
  "properties",
  "shared",
  "sharedWithMeTime",
  "sharingUser",
  "shortcutDetails",
  "size",
  "spaces",
  "starred",
  "teamDriveId",
  "thumbnailLink",
  "thumbnailVersion",
  "trashed",
  "trashedTime",
  "trashingUser",
  "version",
  "viewedByMe",
  "viewedByMeTime",
  "webContentLink",
  "webViewLink",
  "writersCanShare",
];

@Injectable({
  providedIn: "root",
})
export class GoogleDriveApiService {
  requestFields = {
    list: `nextPageToken, files(${FIELDS.join()})`,
    allFields: FIELDS.join(),
    fileInfo: "id,description,webViewLink,name,mimeType,modifiedTime,size,properties",
    shortcutInfo: "id,name,mimeType,shortcutDetails,properties",
    imgInfo: "id,mimeType,thumbnailLink",
  };

  supportsSharedDrives = {
    supportsAllDrives: true,
    includeItemsFromAllDrives: true,
  };

  params = {
    ...this.supportsSharedDrives,
    corpora: "drive",
    driveId: "",
    pageSize: 50,
    fields: this.requestFields.list,
    q: "",
  };

  /** Escapes a value for a single-quoted string literal in a Drive `q` search query. */
  private escapeQueryValue(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  }

  /**
   * Runs a gapi.client.drive request, normalizing rejections into real Error
   * instances (gapi rejects with a plain `{body, headers, result, status, statusText}`
   * object, which reaches Sentry with no stacktrace and no useful message). The status
   * and Google's error message go into the message itself, since Sentry drops a
   * non-Error `cause`.
   */
  private async run<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (err) {
      if (err instanceof Error) throw err;
      const res = err as { status?: number; statusText?: string; result?: { error?: { message?: string } } } | undefined;
      const status = res?.status ?? "unknown status";
      const detail = res?.result?.error?.message ?? res?.statusText ?? "no error message";
      throw new Error(`Google Drive API request failed (${status}): ${detail}`, { cause: err });
    }
  }

  async getThumbnailLink(fileId: string): Promise<string | undefined> {
    const response = await this.run(() => gapi.client.drive.files.get({
      ...this.supportsSharedDrives,
      fields: this.requestFields.imgInfo,
      fileId,
    }));
    const result = response.result;
    console.log("google-drive-api.getThumbnailLink.result", result);
    return result.thumbnailLink;
  }

  /**
   * Get files from a shared drive
   * @param  {string} folderId
   */
  async getFiles(folderId: string): Promise<FileInfo[]> {
    const response = await this.run(() => gapi.client.drive.files.list({
      ...this.supportsSharedDrives,
      pageSize: 1000,
      fields: this.requestFields.list,
      q: `'${folderId}' in parents and trashed = false`,
    }));
    return (response.result.files ?? []).map((file) => FileInfo.fromGoogleFile(file));
  }

  async getFolders(folderId: string): Promise<FileInfo[]> {
    const response = await this.run(() => gapi.client.drive.files.list({
      ...this.supportsSharedDrives,
      pageSize: 1000,
      fields: this.requestFields.list,
      q: `'${folderId}' in parents and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
    }));
    return (response.result.files ?? []).map((file) => FileInfo.fromGoogleFile(file));
  }

  async exportFileAsPdfBlob(fileId: string) {
    const response = await this.run(() => gapi.client.drive.files.export({
      fileId,
      mimeType: "application/pdf",
    }));
    const { body } = response;
    // @ts-ignore
    return Buffer.from(body).toString("base64url");
  }

  /**
   * Drive v3's `alt: "media"` downloads ignore the `fields` parameter (it only ever returns raw
   * bytes, never the metadata JSON), so the file's mime type can't be read off this response —
   * callers that already have the file's `FileInfo.MimeType` (e.g. from `getFiles`/`getFileById`)
   * should use that instead of expecting this method to report it.
   */
  async getFileBlob(fileId: string): Promise<Uint8Array> {
    const response = await this.run(() => gapi.client.drive.files.get({
      ...this.supportsSharedDrives,
      alt: "media",
      fileId,
    }));
    const { body } = response;
    // Convert binary data to a byte array.
    const data = body;
    const len = data.length;
    const ar = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      ar[i] = data.charCodeAt(i);
    }
    return ar;
  }

  async getCsvFile(fileId: string) {
    const response = await this.run(() => gapi.client.drive.files.get({
      ...this.supportsSharedDrives,
      alt: "media",
      fileId,
    }));
    const { result, body } = response;
    const rows = body.split("\n");
    const data = rows.map((row) => row.split(",")).slice(1);

    return data;
  }

  async getFilesByName(fileName: string, parent?: string): Promise<FileInfo[]> {
    const response = await this.run(() => gapi.client.drive.files.list({
      ...this.supportsSharedDrives,
      pageSize: 10,
      fields: this.requestFields.list,
      q: `'${parent}' in parents and name = '${this.escapeQueryValue(fileName)}' and mimeType = 'application/pdf' and trashed = false`,
    }));
    return (response.result.files ?? []).map((file) => FileInfo.fromGoogleFile(file));
  }

  async getFolderByName(folderName: string, parent?: string): Promise<FileInfo[]> {
    const response = await this.run(() => gapi.client.drive.files.list({
      ...this.supportsSharedDrives,
      pageSize: 10,
      fields: this.requestFields.list,
      q: `'${parent}' in parents and name = '${this.escapeQueryValue(folderName)}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
    }));
    return (response.result.files ?? []).map((file) => FileInfo.fromGoogleFile(file));
  }

  async getFileById(fileId: string): Promise<FileInfo> {
    const response = await this.run(() => gapi.client.drive.files.get({
      ...this.supportsSharedDrives,
      fields: this.requestFields.allFields,
      fileId: fileId,
    }));
    return FileInfo.fromGoogleFile(response.result);
  }

  async getFolderByNameSubstring(subString: string, parentFolderId?: string): Promise<FileInfo[]> {
    const response = await this.run(() => gapi.client.drive.files.list({
      ...this.supportsSharedDrives,
      pageSize: 1000,
      fields: this.requestFields.list,
      q: `'${parentFolderId}' in parents and name contains '${this.escapeQueryValue(subString)}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
    }));
    return (response.result.files ?? []).map((file) => FileInfo.fromGoogleFile(file));
  }

  async getChildFolderShortcutListByParentId(parentFolderId: string): Promise<FileInfo[]> {
    const response = await this.run(() => gapi.client.drive.files.list({
      ...this.supportsSharedDrives,
      pageSize: 1000,
      fields: this.requestFields.list,
      q: `'${parentFolderId}' in parents and mimeType = 'application/vnd.google-apps.shortcut' and trashed = false`,
    }));
    return (response.result.files ?? []).map((file) => FileInfo.fromGoogleFile(file));
  }

  async getShortcutsByOriginalId(originalId: string): Promise<FileInfo[]> {
    const response = await this.run(() => gapi.client.drive.files.list({
      ...this.supportsSharedDrives,
      pageSize: 1000,
      fields: this.requestFields.list,
      q: `shortcutDetails.targetId = '${originalId}' and mimeType = 'application/vnd.google-apps.shortcut' and trashed = false`,
    }));
    return (response.result.files ?? []).map((file) => FileInfo.fromGoogleFile(file));
  }

  async renameFile(fileId: string, newName: string) {
    const x = await this.run(() => gapi.client.drive.files.update({
      ...this.supportsSharedDrives,
      fields: this.requestFields.fileInfo,
      fileId: fileId,
      // @ts-ignore
      name: newName,
    }));
    console.log("Google API Update Result Payload:", x.result);
  }

  // WIP - only returns the 59 most recent modified files; using masterAppScript getInvoiceFileId() function to do this task
  async getCustomerInvoiceFiles(invoiceNumber: number): Promise<FileInfo[]> {
    const invoiceNumberString = `${invoiceNumber}`;
    const customerInvoiceFolders = await this.getFolders("1lOlk66JGRub10b-7ATVTjBPukmRkWbcA");
    console.log(customerInvoiceFolders);

    let query = "";
    customerInvoiceFolders.map((subFolder, index) => {
      query += `'${subFolder.Id}' in parents`;
      if (index + 1 !== customerInvoiceFolders.length) {
        query += " or ";
      }
    });
    query += ` and trashed = false`;
    console.log(query);

    let nextPageToken: string | undefined;
    let filteredList: FileInfo[] = [];

    do {
      const response = await this.run(() => gapi.client.drive.files.list({
        ...this.supportsSharedDrives,
        pageSize: 500,
        fields: this.requestFields.list,
        q: query,
        pageToken: nextPageToken,
      }));

      nextPageToken = response.result.nextPageToken;
      const files = (response.result.files ?? [])
        .map((file) => FileInfo.fromGoogleFile(file))
        .filter((file) => file.Name.includes(invoiceNumberString));
      if (files.length > 0) {
        filteredList.push(...files);
      }
      console.log(response.result.files);
      console.log("ran", response.result.nextPageToken, response.result.files?.length);
    } while (nextPageToken);
    return filteredList;
  }

  /**
   * Currently this createFile is specific to uploading csv data from pressure graph charts.
   * @param  {} data
   * @param  {string} fileName?
   * @param  {string} parentFolder?
   */
  async uploadCsvData(data: any, fileName?: string, parentFolder?: string) {
    console.log("drive-api-service.createFile.data", { data, fileName, parentFolder });
    const mimeType = "text/csv";
    const file = data;
    const metadata = {
      name: `${fileName}.csv`, // Filename at Google Drive
      mimeType, // mimeType at Google Drive
      parents: parentFolder ? [parentFolder] : null, // Folder ID at Google Drive
    };

    const accessToken = gapi.auth.getToken().access_token; // Here gapi is used for retrieving the access token.
    const form = new FormData();
    form.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    form.append("file", file);

    const res = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true&convert=true",
      {
        method: "POST",
        headers: new Headers({ Authorization: "Bearer " + accessToken }),
        body: form,
      }
    );
    const val = FileInfo.fromGoogleFile(await res.json());
    console.log("drive-api-service.uploadCsvData.response", val);
    // res.ok ? alert('Upload successful') : alert('Upload failed!');
    return val;
  }

  // Currently this createFile is specific to uploading PNG images from pressure graph.
  async uploadPngImage(data: any, fileName?: string, parentFolder?: string) {
    console.log("drive-api-service.createFile.data", { data, fileName, parentFolder });
    const mimeType = "image/png";
    const file = this.dataURItoBlob(data);
    const metadata = {
      name: `${fileName}.png`, // Filename at Google Drive
      mimeType, // mimeType at Google Drive
      parents: parentFolder ? [parentFolder] : null, // Folder ID at Google Drive
    };

    const accessToken = gapi.auth.getToken().access_token; // Here gapi is used for retrieving the access token.
    const form = new FormData();
    form.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    form.append("file", file);

    const res = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true",
      {
        method: "POST",
        headers: new Headers({ Authorization: "Bearer " + accessToken }),
        body: form,
      }
    );
    const val = FileInfo.fromGoogleFile(await res.json());
    console.log("drive-api-service.uploadPngImage.response", val);
    // res.ok ? alert('Upload successful') : alert('Upload failed!');
    return val;
  }

  dataURItoBlob(dataURI: string) {
    const byteString = atob(dataURI.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: "image/png" });
  }

  /**
   * Copy file
   * @param  {FileInfo} file
   * @param  {string} parent?
   * @param  {string} newName?
   */
  async copyFile(file: FileInfo, parent?: string | null, newName?: string): Promise<FileInfo> {
    console.log("google-drive-api.service.copyFile() running");

    let newFileName = file.Name;

    if (newName) {
      newFileName = newName;
    }

    const response = await this.run(() => gapi.client.drive.files.copy({
      supportsAllDrives: true,
      fileId: file.Id,
      fields: this.requestFields.fileInfo,

      resource: {
        parents: parent ? [parent] : undefined,
        name: newFileName,
      },
    }));
    console.log("copyFile.response.result", response.result);
    return FileInfo.fromGoogleFile(response.result);
  }

  async createFileShortcut(file: FileInfo, parentId: string | null, newName?: string): Promise<FileInfo> {
    console.log("google-drive-api.service.createFileShortcut() running", { file, parentId, newName });

    let newFileName = file.Name;

    if (newName) {
      newFileName = newName;
    }

    const res = await this.run(() => gapi.client.drive.files.create({
      supportsAllDrives: true,
      fields: this.requestFields.shortcutInfo,
      resource: {
        parents: parentId ? [parentId] : undefined,
        name: newFileName,
        mimeType: MIME_TYPE_SHORTCUT,
        shortcutDetails: {
          targetId: file.Id,
          targetMimeType: file.MimeType,
        },
      },
    }));
    console.log("createFileShortcut.response.result", res.result);
    return FileInfo.fromGoogleFile(res.result);
  }

  /**
   * @param  {FileInfo} file
   * @param  {string} parent?
   */
  async moveFile(file: FileInfo, newParentId: string | null, newFileName?: string): Promise<FileInfo> {
    console.log("google-drive-api.service.moveFile() running...");

    const response = await this.run(() => gapi.client.drive.files.update({
      supportsAllDrives: true,
      fileId: file.Id,
      removeParents: file.Parents?.join(","),
      addParents: newParentId ?? undefined,
      fields: this.requestFields.fileInfo,
      resource: {
        name: newFileName || file.Name,
      },
    }));
    console.log("moveFile.response.result", response.result);
    return FileInfo.fromGoogleFile(response.result);
  }

  /**
   * Create folder
   * @param  {string} parentId
   * @param  {string} folderName
   */
  async createFolder(parentId: string, folderName: string): Promise<FileInfo> {
    const folder = {
      name: folderName,
      mimeType: MIME_TYPE_FOLDER,
      parents: [parentId],
    };
    const res = await this.run(() => gapi.client.drive.files.create({
      supportsAllDrives: true,
      resource: folder,
      fields: "id, name, mimeType, modifiedTime, size",
    }));
    return FileInfo.fromGoogleFile(res.result);
  }

  async uploadFile(
    file: any,
    fileName: string,
    parentFolderId: string,
    appProperties?: JtmsDriveAppProperties
  ): Promise<FileInfo> {
    console.log("google-drive-api.service.uploadFile() running...", { file, fileName, parentFolderId });
    const metadata = {
      name: fileName,
      mimeType: file.type,
      parents: [parentFolderId],
      ...(appProperties ? { appProperties } : {}),
    };
    const accessToken = gapi.auth.getToken().access_token; // Here gapi is used for retrieving the access token.
    const form = new FormData();
    form.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    form.append("file", file);
    const res = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true",
      {
        method: "POST",
        headers: new Headers({ Authorization: "Bearer " + accessToken }),
        body: form,
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => undefined);
      throw new Error(`Google Drive upload failed (${res.status}): ${body?.error?.message ?? res.statusText}`);
    }
    const val = FileInfo.fromGoogleFile(await res.json());
    console.log("drive-api-service.uploadFile.response", val);
    return val;
  }

  async delete(fileId: string) {
    console.log("google-drive-api.service.delete() running...", fileId);
    return this.run(() => gapi.client.drive.files.delete({
      supportsAllDrives: true,
      fields: this.requestFields.fileInfo,
      fileId: fileId,
    }));
  }

  uuid() {
    //@ts-ignore
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: number) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
  }
}
